/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import axios from 'axios'
import { injectable } from 'inversify'
import { IAccountFacilityApiClient } from './contracts'
import { IAccountFacilityDetail, IAccountFacilityGridCriteria, IAccountFacilityGridResults, IUserFacilityGridCriteria, IUserFacilityGridResults } from '../AccountFacilityStateManager/contracts'
import { SettingsManager } from '../../configuration/SettingsManager'
import { injectProperty } from '../../arc/configuration/ServiceLocator'
import { IUserSessionMiddleware } from '../../arc/middleware/UserSessionMiddleware/contracts'
import { ErrorHelper } from '../../utils/ErrorHelper'
import { INpi } from '../RequestHistoryStateManager/contracts'

@injectable()
export class AccountFacilityApiClient implements IAccountFacilityApiClient {
	@injectProperty('IUserSessionMiddleware')
	private readonly userSessionMiddleware!: IUserSessionMiddleware

	list(criteria: IAccountFacilityGridCriteria): Promise<IAccountFacilityGridResults> {
		let fieldName = ''
		let operatorValue = ''
		if (criteria.searchTerm.trim().length == 10 && !isNaN(parseInt(criteria.searchTerm))) {
			fieldName = 'Npi'
			operatorValue = '=='
		} else {
			fieldName = 'FacilityName'
			operatorValue = '%%'
		}
		const sortField = criteria.sortField.charAt(0).toUpperCase() + criteria.sortField.slice(1)
		const sort = `${criteria.sortOrder === 'asc' ? '%2b' : '-'}${sortField}`
		//criteria.page = criteria.searchTerm ? 1 : criteria.page
		const offset = (criteria.page - 1) * criteria.sizePerPage
		const limit = criteria.sizePerPage
		const filter = criteria.searchTerm ? `filter=${encodeURIComponent(JSON.stringify({ filters: [{ field: fieldName, operator: operatorValue, value: criteria.searchTerm }] }))}` : ''
		const dateTimeStamp = `t=${Date.now()}`
		const params = `Sort=${sort}&Offset=${offset}&Limit=${limit}&${filter}&${dateTimeStamp}`
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/customerentityfacilities?${params}`
		return this.userSessionMiddleware
			.loadSessionJwt()
			.then(token =>
				axios.get(url, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}),
			)
			.then(response => response.data)
			.catch(error => {
				ErrorHelper.handleApiError(error)
			})
	}

	listUsers(id: number, criteria: IUserFacilityGridCriteria): Promise<IUserFacilityGridResults> {
		const dateTimeStamp = `t=${Date.now()}`
		const sortField = criteria.sortField.charAt(0).toUpperCase() + criteria.sortField.slice(1)
		const sort = `${criteria.sortOrder === 'asc' ? '%2b' : '-'}${sortField}`
		const offset = (criteria.page - 1) * criteria.sizePerPage
		const limit = criteria.sizePerPage
		const params = `Sort=${sort}&Offset=${offset}&Limit=${limit}&${dateTimeStamp}`
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/customerentityfacilities/${id}/users/?${params}`
		return this.userSessionMiddleware
			.loadSessionJwt()
			.then(token =>
				axios.get(url, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}),
			)
			.then(response => response.data)
			.catch(error => {
				ErrorHelper.handleApiError(error)
			})
	}

	getAccountFacility(id: number): Promise<IAccountFacilityDetail> {
		const dateTimeStamp = `t=${Date.now()}`
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/customerentityfacilities/${id}/facility?${dateTimeStamp}`
		return this.userSessionMiddleware
			.loadSessionJwt()
			.then(token =>
				axios.get(url, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}),
			)
			.then(response => response.data)
			.catch(error => {
				ErrorHelper.handleApiError(error)
			})
	}

	saveAccountFacility(item: IAccountFacilityDetail): Promise<void> {
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/customerentityfacilities`
		return this.userSessionMiddleware
			.loadSessionJwt()
			.then(token =>
				axios.post(url, item, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}),
			)
			.then(response => response.data)
			.catch(error => {
				ErrorHelper.handleApiError(error)
			})
	}

	updateAccountFacility(item: IAccountFacilityDetail): Promise<void> {
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/customerentityfacilities/${item.id}`
		return this.userSessionMiddleware
			.loadSessionJwt()
			.then(token =>
				axios.put(url, item, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}),
			)
			.then(response => response.data)
			.catch(error => {
				ErrorHelper.handleApiError(error)
			})
	}

	getAllNpis(): Promise<INpi[]> {
		const dateTimeStamp = `t=${Date.now()}`
		const url = `${SettingsManager.REACT_APP_MAIN_API_BASE_URL().value}/customerentityfacilities/getNpisForFilter?${dateTimeStamp}`
		return this.userSessionMiddleware.loadSessionJwt().then(token =>
			axios
				.get(url, {
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
				.then(response => response.data.items)
				.catch(error => {
					ErrorHelper.handleApiError(error)
				}),
		)
	}
}
